<template>
  <vl-region :key="key">
    <vl-layout>
      <PbsGoBackLink />
      <template v-if="!loading">
        <vl-grid>
          <vl-column width="6" width-xs="12">
            <vl-title tag-name="h1">
              {{ $t('deliveryprocessdefinition.detail.title') }} <PbsStatusIcon v-if="detail" :id="detail?.statusId" />
            </vl-title>
          </vl-column>
          <vl-column width="6" width-xs="12">
            <vl-action-group
              v-if="detail && !editStore.isEdit(DeliveryProcessDefinitionEditType.DeliveryProcessDefinition, detail.id)"
              mod-align-right
              mod-space
              between
            >
              <vl-button
                v-if="editStore.canEdit"
                icon="pencil"
                mod-icon-before
                mod-secondary
                :mod-disabled="editStore.editing"
                @click="editStore.openEdit(DeliveryProcessDefinitionEditType.DeliveryProcessDefinition, detail.id)"
              >
                {{ $t('general.edit') }}
              </vl-button>
              <DefinitionActionButton :detail="detail" :actions="actionButtonTypes" mod-large />
            </vl-action-group>
            <vl-action-group v-else mod-align-right mod-space between>
              <vl-button icon="save" :mod-loading="reloading" mod-icon-before @click="save">{{
                $t('general.save')
              }}</vl-button>
              <vl-button
                icon="cross"
                :mod-loading="loading"
                mod-icon-before
                mod-secondary
                mod-error
                @click="editStore.closeEdit"
              >
                {{ $t('general.cancel') }}
              </vl-button>
            </vl-action-group>
          </vl-column>
        </vl-grid>
        <vl-grid
          v-if="detail"
          :class="{
            edit: editStore.isEdit(DeliveryProcessDefinitionEditType.DeliveryProcessDefinition, detail.id),
            'vl-u-spacer-bottom--medium': true
          }"
        >
          <vl-column>
            <DeliveryProcessDefinitionEdit
              v-if="editStore.isEdit(DeliveryProcessDefinitionEditType.DeliveryProcessDefinition, detail.id)"
              ref="editForm"
            />
            <DeliveryProcessDefinitionRead v-else />
          </vl-column>
        </vl-grid>
        <template v-if="detail && !loading">
          <DeliverySpecification :detail="detail" />
          <div class="vl-u-spacer--large vl-u-visible--s"></div>
          <OperationSpecification :detail="detail" />
          <div class="vl-u-spacer--large vl-u-visible--s"></div>
          <DeliveryTransformation />
          <template v-if="canEditDataMapping">
            <div class="vl-u-spacer--large vl-u-visible--s"></div>
            <OperationEntityDataMapping :detail="detail" />
          </template>
        </template>
      </template>
      <template v-if="loading">
        <vl-region>
          <div v-vl-align:center>
            <vl-loader :message="$t('deliveryprocessdefinition.detail.loading')" />
          </div>
        </vl-region>
      </template>
    </vl-layout>
  </vl-region>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useRoute, onBeforeRouteLeave } from 'vue-router';
import { useDeliveryProcessDefinitionDetailStore } from '../../store/delivery-process-definition/delivery-process-definition-detail.store';
import { DeliveryProcessDefinitionDetailDto } from '@/api/backoffice/clients';
import DeliverySpecification from './subcomponents/delivery-specification/DeliverySpecification.vue';
import DeliveryProcessDefinitionRead from './DeliveryProcessDefinitionRead.vue';
import OperationSpecification from './subcomponents/operation-specification/OperationSpecification.vue';
import DeliveryTransformation from './subcomponents/delivery-transformation/DeliveryTransformation.vue';
import OperationEntityDataMapping from './subcomponents/operation-entity-data/OperationEntityDataMapping.vue';
import DeliveryProcessDefinitionEdit from './DeliveryProcessDefinitionEdit.vue';
import {
  DeliveryProcessDefinitionEditType,
  useDeliveryProcessDefinitionEditStore
} from '../../store/delivery-process-definition/delivery-process-definition-edit.store';
import { useAlertStore } from '@/modules/core/store/alert.store';
import DefinitionActionButton from './subcomponents/definition-action-button/DefinitionActionButton.vue';
import PbsGoBackLink from '@/components/PbsGoBackLink.vue';
import PbsStatusIcon from '@/components/PbsStatusIcon.vue';
import { DeliveryProcessDefinitionActionButtonType } from '../../common/delivery-process-definition.common';

const store = useDeliveryProcessDefinitionDetailStore();
const editStore = useDeliveryProcessDefinitionEditStore();

const route = useRoute();

const editForm = ref();

const key = ref(route.params.id);

watch(
  () => route.params.id,
  (newKey) => {
    if (key.value !== newKey) {
      key.value = newKey;
      getDetail();
    }
  }
);

const detail = computed((): DeliveryProcessDefinitionDetailDto => {
  return store.detail;
});

const actionButtonTypes = computed(() => {
  return [
    DeliveryProcessDefinitionActionButtonType.ChangeStatus,
    DeliveryProcessDefinitionActionButtonType.DryRun,
    DeliveryProcessDefinitionActionButtonType.Export,
    DeliveryProcessDefinitionActionButtonType.GenerateReport,
    DeliveryProcessDefinitionActionButtonType.GeckoConfiguration,
    DeliveryProcessDefinitionActionButtonType.Duplicate,
    DeliveryProcessDefinitionActionButtonType.Dependencies,
    DeliveryProcessDefinitionActionButtonType.CascadeDelete
  ];
});

const parentDefinitionId = computed((): number => {
  return detail.value?.parent?.id;
});

const loading = computed((): boolean => {
  return store.loading;
});

const reloading = computed((): boolean => {
  return store.reloading;
});

const canEditDataMapping = computed((): boolean => {
  return editStore.canEdit && parentDefinitionId.value > 0;
});

const id = computed((): number => {
  return parseInt(route.params.id as string, 10);
});

onMounted(async () => {
  await getDetail();
});

const save = () => {
  editForm.value.onSubmit();
};

const getDetail = async () => {
  editStore.init();
  useAlertStore().clearAlert();
  await store.get(id.value);
};

onBeforeRouteLeave((to, from, next) => {
  const id = parseInt(from.params.id as string, 10);
  if (id && store) {
    store.clear(id);
    editStore.closeEdit();
  }
  next();
});
</script>
